import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { navigateToWithState, showToastMessage } from "../../../../utils/commonFunctions";
import ConfirmationDialog from "../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import MissionService from "../../../../service/MissionService";
import { TmState, ValidationRule, TestCaseStep, TelemetryEntry, TelemetryApiResponse, ValidationRuleEntry } from "./TestCaseTypes";
import {
  flattenPayload,
  buildNestedValidation,
  reconstructParamStates,
  flattenSchemaEnumMappings,
  flattenSchemaTypes,
  exportTestCaseValidations,
  parseExportedValidations,
  importValidationsFromFile,
} from "./TestCaseHelpers";
import LeftPanel from "./LeftPanel";
import PreviewPanel from "./PreviewPanel";
import StepsDialog from "./StepsDialog";
import { SelectOption } from "./TestCaseSelectStyles";
import {
  getInitialTmStateFromValidationConfig,
  toTelemetryEntry,
  parseValidationConfigResponse,
} from "./ValidationConfigHelpers";
import { SingleValue, MultiValue } from "react-select";
import "./TestCaseDetail.scss";

const normalizeId = (value: unknown): string => value == null ? "" : String(value).trim();

const getIdentityPart = (value: unknown): string => normalizeId(value) || "na";

// Composite key used for TM deduplication and matching.
// All five fields are required to uniquely identify a TM — commandName alone is not enough
// because the same command can exist under different subsystems/saIds in the catalog.
const getTelemetryIdentity = (telemetry: Partial<TelemetryEntry> | null | undefined): string => [
  getIdentityPart(telemetry?.commandId),
  getIdentityPart(telemetry?.saId),
  getIdentityPart(telemetry?.daId),
  getIdentityPart(telemetry?.subsystem),
  getIdentityPart(telemetry?.commandName),
].join("::");

const normalizeText = (value: unknown): string => normalizeId(value).toLowerCase();

const normalizeMatchText = (value: unknown): string => normalizeText(value).replace(/[^a-z0-9]+/g, "");

const parseCommandNameFromLabel = (label: string | undefined): string => {
  const raw = label ?? "";
  const parts = raw.split(" - ");
  return parts.length > 1 ? parts.slice(1).join(" - ") : raw;
};

const getTelemetryOptionIdentity = (option: SelectOption): string => getTelemetryIdentity(option.value as Partial<TelemetryEntry>);

// Returns TMs to auto-select when a TC is chosen:
// - If there are recommended TMs, return all of them.
// - If there is exactly 1 TM in the entire list, auto-select it (unambiguous match).
// - Otherwise return nothing — let the user pick manually.
const getAutoTelemetryOptions = (options: SelectOption[]): SelectOption[] => {
  const recommended = options.filter((option) => option.isRecommended);
  if (recommended.length > 0) return recommended;
  return options.length === 1 ? options : [];
};

// Full identity is the dedup key so two TMs with the same commandName but different saId/subsystem
// are kept as separate entries in the dropdown instead of being collapsed into one.
const getTelemetryDisplayKey = (telemetry: TelemetryEntry): string => getTelemetryIdentity(telemetry) || normalizeMatchText(telemetry.commandName);

const getTelemetryLabel = (telemetry: TelemetryEntry): string => normalizeId(telemetry.commandName) || "Unnamed TM";

const dedupeTelemetryEntries = (telemetries: TelemetryEntry[]): TelemetryEntry[] => {
  const seen = new Set<string>();
  return telemetries.filter((telemetry) => {
    const key = getTelemetryDisplayKey(telemetry);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getCommandId = (command: SelectOption | null): unknown => {
  const commandValue = command?.value as Record<string, unknown> | undefined;
  const sequenceData = commandValue?.sequenceData as Record<string, unknown> | undefined;
  return sequenceData?.commandId;
};

const decodeName = (value: string | undefined): string => {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const convertArraysToIndexedObjects = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.reduce<Record<string, unknown>>((acc, item, index) => {
      acc[String(index)] = convertArraysToIndexedObjects(item);
      return acc;
    }, {});
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, nested]) => {
      acc[key] = convertArraysToIndexedObjects(nested);
      return acc;
    }, {});
  }

  return value;
};

// ─── Main Component ───────────────────────────────────────────
const TestCaseDetail = (): JSX.Element => {
  const params = useParams<{
    mission_name: string;
    mission_id: string;
    sat_name: string;
    sat_id: string;
    test_case_name: string;
    test_case_id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { isEdit?: boolean; testCaseId?: string; satelliteId?: string } | null;
  const isEditMode = locationState?.isEdit === true;
  const editTestCaseId = locationState?.testCaseId ?? params.test_case_id;
  const passedSatelliteId = locationState?.satelliteId;
  const { mission_name, mission_id, sat_name, sat_id, test_case_name } = params;

  // ── Left panel selections ──
  const [busComponents, setBusComponents] = useState<SelectOption[]>([]);
  const [component, setComponent] = useState<SelectOption | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<SelectOption | null>(null);
  const [selectedTelemetries, setSelectedTelemetries] = useState<SelectOption[]>([]);
  const [allTelemetries, setAllTelemetries] = useState<TelemetryEntry[]>([]);
  const [loadingComponents, setLoadingComponents] = useState<boolean>(!!mission_id && !!sat_id);
  const [loadingTelemetries, setLoadingTelemetries] = useState<boolean>(!!mission_id && !!sat_id);

  // ── Scheduling ──
  const [delay, setDelay] = useState<string>("6000");
  const [timeout, setTimeout] = useState<string>("5000");
  const [isCritical, setIsCritical] = useState<boolean>(false);

  // ── UI state ──
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);
  const [showStepsDialog, setShowStepsDialog] = useState<boolean>(isEditMode);
  const [editableTestCaseName, setEditableTestCaseName] = useState<string>(decodeName(test_case_name));

  // ── Preview panel ──
  const [tcJsonText, setTcJsonText] = useState<string>("");
  const [isJsonValid, setIsJsonValid] = useState<boolean>(true);
  const [activePreviewTab, setActivePreviewTab] = useState<"json" | "validations" | "preview">("validations");
  const [activeTcPreviewTab, setActiveTcPreviewTab] = useState<"json" | "form">("form");
  const [expandedTMs, setExpandedTMs] = useState<string[]>([]);
  const [paramStates, setParamStates] = useState<Record<string, TmState>>({});
  const [testCaseSteps, setTestCaseSteps] = useState<TestCaseStep[]>([]);

  // ── Resolved satellite ID (satellites[0].uniqueId, not the group uniqueId) ──
  const [satelliteId, setSatelliteId] = useState<string>("");

  // ── Edit mode (from GET API) ──
  const [editRawData, setEditRawData] = useState<Record<string, unknown> | null>(null);
  const [loadingEditSteps, setLoadingEditSteps] = useState<boolean>(isEditMode);

  // ── Step edit snapshot (tracks a step removed for editing so it can be restored on discard) ──
  const [editingStepSnapshot, setEditingStepSnapshot] = useState<{ step: TestCaseStep | null; idx: number } | null>(null);

  // ── Blink the step row after save/discard ──
  const [blinkStepIdx, setBlinkStepIdx] = useState<number | number[] | null>(null);
  const blinkStepTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!editableTestCaseName.trim()) {
      setEditableTestCaseName(decodeName(test_case_name));
    }
  }, [test_case_name]);

  // ── Edit restore refs ──
  const telemetriesToRestoreRef = useRef<SelectOption[]>([]);
  const paramStatesToRestoreRef = useRef<Record<string, TmState>>({});
  const tcJsonToRestoreRef = useRef<string | null>(null);
  const initialStepsSignatureRef = useRef<string>("[]");
  // Guard to prevent the edit-reconstruction effect from re-running after the user
  // has already seen (and potentially navigated away from) the initial preview.
  const editReconstructedRef = useRef<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoTelemetrySelectionKeyRef = useRef<string>("");
  const skipNextTelemetryAutoSelectRef = useRef<boolean>(false);
  // Track the previous commandId to detect actual command changes vs. object reference changes
  const prevCommandIdRef = useRef<number | undefined>(undefined);

  const findSubsystemByCommand = (command: SelectOption | null): SelectOption | null => {
    if (!command) return null;

    const commandValue = command.value as Record<string, unknown> | undefined;
    const commandSeq = commandValue?.sequenceData as Record<string, unknown> | undefined;
    const commandId = commandSeq?.commandId as number | undefined;
    const commandName = normalizeText((commandValue?.name as string | undefined) ?? parseCommandNameFromLabel(command.label));
    const commandLabel = normalizeText(command.label);

    for (const comp of busComponents) {
      if (comp.label === "All") continue;
      const cmds = comp.value as SelectOption[];
      if (!Array.isArray(cmds)) continue;

      const matched = cmds.some((cmd) => {
        const cmdValue = cmd.value as Record<string, unknown> | undefined;
        const cmdSeq = cmdValue?.sequenceData as Record<string, unknown> | undefined;
        const cmdId = cmdSeq?.commandId as number | undefined;
        const cmdName = normalizeText((cmdValue?.name as string | undefined) ?? parseCommandNameFromLabel(cmd.label));
        const cmdLabel = normalizeText(cmd.label);

        if (commandId != null && cmdId != null) {
          if (commandId !== cmdId) return false;
          if (commandName && cmdName) return commandName === cmdName;
          return true;
        }
        if (commandName && cmdName) return commandName === cmdName;
        return !!commandLabel && commandLabel === cmdLabel;
      });

      if (matched) return comp;
    }

    return null;
  };

  const getStepsSignature = (steps: TestCaseStep[]): string => {
    return JSON.stringify(
      steps.map((step) => ({
        subsystem: step.subsystem?.label ?? "",
        teleData: step.teleData?.label ?? "",
        delay: step.delay,
        timeout: step.timeout,
        tcJson: step.tcJson,
        validationRule: step.validationRule,
      }))
    );
  };

  const parseNonNegativeMs = (value: string | undefined): number => {
    const parsed = Number.parseInt(value ?? "", 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  };

  const normalizeMsInput = (value: string): string => String(parseNonNegativeMs(value));

  // ─── Data loading ─────────────────────────────────────────────
  const getCommands = (buscommands: Record<string, unknown>[]): SelectOption[] => {
    const commandMap = new Map<string, SelectOption>();
    buscommands.forEach((comm) => {
      const sequenceData = comm?.sequenceData as Record<string, unknown> | undefined;
      const uniqueKey = [
        (comm?.funcCategory as string) || "uncategorized",
        (sequenceData?.commandId as string) || "no-id",
        (comm?.name as string) || "unnamed",
      ].join("::");
      if (!commandMap.has(uniqueKey)) {
        commandMap.set(uniqueKey, {
          label: `${(sequenceData?.commandId as string) || "N/A"} - ${comm?.name as string}`,
          value: comm,
        });
      }
    });
    return Array.from(commandMap.values());
  };

  const getAdhocComponents = (): void => {
    setLoadingComponents(true);
    MissionService.getAdhocComponents()
      .then((res: { data: Record<string, Record<string, unknown>[]> }) => {
        if (res.data) {
          const componentMap = new Map<string, SelectOption>();
          Object.keys(res.data).forEach((key) => {
            const normalizedKey = key.trim().toLowerCase();
            if (!componentMap.has(normalizedKey)) {
              componentMap.set(normalizedKey, { label: key, value: getCommands(res.data[key]) });
            }
          });
          const subsystems = Array.from(componentMap.values());
          const allCommands = subsystems.flatMap((c) => c.value as SelectOption[]);
          const allOptions: SelectOption[] = [{ label: "All", value: allCommands }, ...subsystems];
          setBusComponents(allOptions);
          if (!isEditMode) setComponent(allOptions[0]);
        }
      })
      .catch((err: unknown) => console.log(err))
      .finally(() => setLoadingComponents(false));
  };

  useEffect(() => {
    if (!mission_id || !sat_id) return;
    getAdhocComponents();

    const loadWithSatelliteId = (resolvedId: string) => {
      setSatelliteId(resolvedId);
      setLoadingTelemetries(true);

      const loadValidationConfigs = () => {
        MissionService.getValidationConfigs(mission_id, resolvedId)
          .then((r: any) => {
            const parseResult = parseValidationConfigResponse(r?.data);
            if (parseResult.success) {
              const telemetries = parseResult.configs
                .map(toTelemetryEntry)
                .filter((t): t is TelemetryEntry => t !== null);
              setAllTelemetries(telemetries);
            } else {
              console.warn("Validation Config API:", parseResult.error || "No configs returned");
              setAllTelemetries([]);
            }
          })
          .catch((err: unknown) => {
            console.warn("Validation Config API error:", err instanceof Error ? err.message : err);
            setAllTelemetries([]);
          })
          .finally(() => setLoadingTelemetries(false));
      };

      loadValidationConfigs();

      if (isEditMode && editTestCaseId) {
        MissionService.getValidateTestCaseById(mission_id, editTestCaseId, resolvedId)
          .then((r: { data: { data: Record<string, unknown> } }) => setEditRawData(r.data.data))
          .catch((err: unknown) => console.log(err));
      }
    };

    if (passedSatelliteId) {
      loadWithSatelliteId(sat_id);
    } else {
      MissionService.get_satellite_by_sat_id(sat_id, mission_id)
        .then((res: any) => loadWithSatelliteId(sat_id))
        .catch(() => loadWithSatelliteId(sat_id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      initialStepsSignatureRef.current = "[]";
    }
  }, [isEditMode]);

  // ── Reconstruct steps from API response in edit mode ──────────
  useEffect(() => {
    if (!isEditMode || !editRawData) return;
    // Wait for busComponents AND allTelemetries to settle before reconstructing (subsystem lookup
    // requires busComponents; validation payloadSchema re-attachment requires allTelemetries — if we
    // reconstruct while allTelemetries is still empty, each step's TM gets an empty payloadSchema
    // baked in, which hides configured validations until the telemetry dropdown is touched again).
    // After the first successful reconstruction, never re-run — this prevents the flicker where
    // a late-arriving allTelemetries response triggers setShowStepsDialog(true) and overrides
    // any navigation the user has already performed (e.g. clicking "Add Telecommand").
    if (busComponents.length === 0 || loadingTelemetries || editReconstructedRef.current) return;
    const details = (editRawData.testCaseDetails as Record<string, unknown>[]) ?? [];
    const steps: TestCaseStep[] = details.map((detail) => {
      const entryDetails = (detail.testCaseEntryDetails ?? {}) as Record<string, unknown>;
      const seqData = entryDetails.sequenceData as Record<string, unknown> | undefined;
      const commandName = (entryDetails.commandName as string) ?? "";
      const commandId = seqData?.commandId;
      const teleData: SelectOption = {
        label: commandId != null ? `${commandId} - ${commandName}` : commandName,
        value: { name: commandName, sequenceData: seqData },
      };
      // Look up busComponent to get the proper commands array as value
      const subsystemLabel = detail.subsystem as string | undefined;
      const matchedComponent = subsystemLabel
        ? busComponents.find((c) => c.label.toLowerCase() === subsystemLabel.toLowerCase()) ?? { label: subsystemLabel, value: [] as SelectOption[] }
        : null;
      const validationRule: ValidationRuleEntry[] = ((detail.validations ?? []) as Record<string, unknown>[]).map((v) => {
        const vCommandId = v.commandId as number;
        // Look up original telemetry to get the real payload schema for param display
        const validationTelemetry: Partial<TelemetryEntry> = {
          commandId: vCommandId,
          saId: v.saId as number,
          daId: v.daId as number,
          subsystem: v.subsystem as string,
          commandName: v.commandName as string,
        };
        const originalTm = allTelemetries.find((t) => getTelemetryIdentity(t) === getTelemetryIdentity(validationTelemetry)) ?? null;
        const tmValue: TelemetryEntry = originalTm
          ? { ...originalTm, mode: (v.mode as number | null | undefined) ?? originalTm.mode }
          : ({ commandId: vCommandId, saId: v.saId as number, daId: v.daId as number, subsystem: v.subsystem as string, commandName: v.commandName as string, mode: v.mode as number | null | undefined, payload: {}, payloadSchema: { type: "object", properties: {} } } as TelemetryEntry);
        return {
          label: v.commandName as string,
          value: tmValue,
          validation: v.payload as Record<string, unknown>,
        };
      });
      return {
        subsystem: matchedComponent,
        teleData,
        delay: String((entryDetails.delayAfterMS as number) ?? 0),
        timeout: String((entryDetails.timeoutMS as number) ?? 0),
        tcJson: seqData ? JSON.stringify(seqData, null, 2) : "",
        validationRule,
        isCritical: !!detail.isCritical,
      };
    });
    setTestCaseSteps(steps);
    initialStepsSignatureRef.current = getStepsSignature(steps);
    editReconstructedRef.current = true;
    setShowStepsDialog(true);
    setLoadingEditSteps(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTelemetries, editRawData, busComponents, loadingTelemetries]);

  // ── Restore state when telecommand changes ────────────────────
  useEffect(() => {
    const currentCommandId = (selectedCommand?.value as Record<string, unknown> | undefined)?.sequenceData
      ? ((selectedCommand?.value as Record<string, unknown>).sequenceData as Record<string, unknown>)?.commandId as number | undefined
      : undefined;

    // Only reset state if the command ID actually changed (not just the object reference)
    const commandChanged = prevCommandIdRef.current !== currentCommandId;
    prevCommandIdRef.current = currentCommandId;

    if (tcJsonToRestoreRef.current !== null) {
      setTcJsonText(tcJsonToRestoreRef.current);
      setIsJsonValid(true);
      setActiveTcPreviewTab("form");
      tcJsonToRestoreRef.current = null;
    } else if (selectedCommand) {
      const json = (selectedCommand.value as Record<string, unknown> | undefined)?.sequenceData;
      setTcJsonText(json ? JSON.stringify(json, null, 2) : "");
      setIsJsonValid(true);
      setActiveTcPreviewTab("form");
    } else {
      setTcJsonText("");
      setActiveTcPreviewTab("form");
    }

    if (commandChanged) {
      if (telemetriesToRestoreRef.current.length > 0) {
        skipNextTelemetryAutoSelectRef.current = true;
        setSelectedTelemetries(telemetriesToRestoreRef.current);
        telemetriesToRestoreRef.current = [];
      } else {
        setSelectedTelemetries([]);
        autoTelemetrySelectionKeyRef.current = "";
      }
      if (Object.keys(paramStatesToRestoreRef.current).length > 0) {
        setParamStates(paramStatesToRestoreRef.current);
        paramStatesToRestoreRef.current = {};
      } else {
        setParamStates({});
      }
      setExpandedTMs([]);
    }
  }, [selectedCommand]);

  // ─── Handlers ────────────────────────────────────────────────
  const handleComponentChange = (option: SingleValue<SelectOption>): void => {
    setComponent(option);
    setSelectedCommand(null);
    setSelectedTelemetries([]);
    autoTelemetrySelectionKeyRef.current = "";
  };

  const resolveSelectedSubsystemLabel = (command: SelectOption | null, currentComponent: SelectOption | null): string | undefined => {
    if (currentComponent?.label && currentComponent.label !== "All") return currentComponent.label;
    return findSubsystemByCommand(command)?.label;
  };

  const getTelecommandName = (command: SelectOption | null): string => {
    const commandValue = command?.value as Record<string, unknown> | undefined;
    return (commandValue?.name as string | undefined) ?? parseCommandNameFromLabel(command?.label);
  };

  // A TM is an "exact match" (shown as Recommended) only when commandId, commandName,
  // AND subsystem all agree with the selected TC. Subsystem check is skipped when the
  // TC subsystem cannot be resolved (e.g. "All" selected and no match in busComponents).
  const isExactTelemetryMatch = (telemetry: TelemetryEntry, command: SelectOption | null, subsystemLabel: string | undefined): boolean => {
    const commandId = getCommandId(command);
    const commandIdKey = normalizeId(commandId);
    const telemetryName = normalizeMatchText(telemetry.commandName);
    const commandName = normalizeMatchText(getTelecommandName(command));

    if (!telemetryName || !commandName || telemetryName !== commandName) return false;
    if (commandIdKey && normalizeId(telemetry.commandId) !== commandIdKey) return false;

    if (subsystemLabel && normalizeText(telemetry.subsystem) !== normalizeText(subsystemLabel)) {
      return false;
    }

    return true;
  };

  // Builds the three-tier TM dropdown for the selected TC:
  //   Recommended  — exact match on commandId + commandName + subsystem
  //   Relevant     — same commandId/subsystem but name doesn't exactly match (or vice versa)
  //   Unassociated — everything else from the full catalog
  // subsystemLabel is used when available; if absent (e.g. subsystem is empty in catalog)
  // matching falls back to commandId only.
  const getTelemetryOptionsForCommand = (command: SelectOption | null, currentComponent: SelectOption | null): SelectOption[] => {
    const commandId = getCommandId(command);
    const commandIdKey = normalizeId(commandId);
    const subsystemLabel = resolveSelectedSubsystemLabel(command, currentComponent);

    const allUniqueTelemetries = dedupeTelemetryEntries(allTelemetries);
    const relevantTelemetries = commandIdKey
      ? dedupeTelemetryEntries(
          allTelemetries.filter((telemetry) => {
            if (normalizeId(telemetry.commandId) !== commandIdKey) return false;
            if (subsystemLabel) {
              return normalizeText(telemetry.subsystem) === normalizeText(subsystemLabel);
            }
            return true;
          })
        )
      : [];

    const exactMatches = relevantTelemetries.filter((telemetry) => isExactTelemetryMatch(telemetry, command, subsystemLabel));
    // If there are exact matches use them; if there's exactly one relevant TM it's unambiguous enough to recommend.
    const recommendedTelemetries = exactMatches.length > 0
      ? exactMatches
      : relevantTelemetries.length === 1 ? relevantTelemetries : [];
    const recommendedIds = new Set(recommendedTelemetries.map((telemetry) => getTelemetryIdentity(telemetry)));
    const extraRelevantTelemetries = relevantTelemetries.filter((telemetry) => !recommendedIds.has(getTelemetryIdentity(telemetry)));
    // Exclude relevant TMs from unassociated list using full identity key to avoid
    // same-name TMs in different subsystems being wrongly excluded.
    const relevantDisplayKeys = new Set(relevantTelemetries.map((telemetry) => getTelemetryDisplayKey(telemetry)));
    const unassociatedTelemetries = allUniqueTelemetries.filter((telemetry) => !relevantDisplayKeys.has(getTelemetryDisplayKey(telemetry)));

    const recommendedOptions: SelectOption[] = recommendedTelemetries.map((telemetry, idx) => ({
      label: getTelemetryLabel(telemetry),
      value: telemetry,
      isRecommended: true,
      sectionLabel: idx === 0 ? (recommendedTelemetries.length > 1 ? "Recommended TMs" : "Recommended TM") : undefined,
    }));

    const extraOptions: SelectOption[] = extraRelevantTelemetries.map((telemetry, idx) => ({
      label: getTelemetryLabel(telemetry),
      value: telemetry,
      isSecondary: recommendedOptions.length > 0,
      sectionLabel: idx === 0 ? (recommendedOptions.length > 0 ? "Secondary TMs" : "Relevant TMs") : undefined,
      showSectionDivider: idx === 0 && recommendedOptions.length > 0,
    }));

    const unassociatedOptions: SelectOption[] = unassociatedTelemetries.map((telemetry, idx) => ({
      label: getTelemetryLabel(telemetry),
      value: telemetry,
      isUnassociated: true,
      sectionLabel: idx === 0 ? "Unassociated TMs" : undefined,
      showSectionDivider: idx === 0 && (recommendedOptions.length > 0 || extraOptions.length > 0),
    }));

    return [...recommendedOptions, ...extraOptions, ...unassociatedOptions];
  };

  const getTelemetryAutoSelectionKey = (command: SelectOption | null, options: SelectOption[]): string => {
    const commandId = normalizeId(getCommandId(command)) || "na";
    return [
      commandId,
      resolveSelectedSubsystemLabel(command, component) ?? "na",
      options.map(getTelemetryOptionIdentity).join("|"),
      options.filter((option) => option.isRecommended).map(getTelemetryOptionIdentity).join("|"),
    ].join("::");
  };

  const handleTelecommandChange = (option: SingleValue<SelectOption>): void => {
    const nextTelemetryOptions = getTelemetryOptionsForCommand(option, component);
    const autoTelemetryOptions = getAutoTelemetryOptions(nextTelemetryOptions);
    autoTelemetrySelectionKeyRef.current = autoTelemetryOptions.length > 0 ? getTelemetryAutoSelectionKey(option, nextTelemetryOptions) : "";
    // Route through the restore refs so the effect below commits selection + paramStates atomically, avoiding the reset-then-reseed race.
    telemetriesToRestoreRef.current = autoTelemetryOptions;
    paramStatesToRestoreRef.current = autoTelemetryOptions.reduce<Record<string, TmState>>((acc, tm) => {
      acc[tm.label] = getInitialTmStateFromValidationConfig(tm.value) ?? { checkedParams: [], validations: {} };
      return acc;
    }, {});
    setSelectedCommand(option);
  };

  const handleJsonChange = (text: string): void => {
    setTcJsonText(text);
    try {
      const parsed = JSON.parse(text);
      const expectedId = (selectedCommand?.value as Record<string, unknown> | undefined)?.sequenceData
        ? ((selectedCommand?.value as Record<string, unknown>).sequenceData as Record<string, unknown>)?.commandId
        : undefined;
      if (expectedId !== undefined && parsed.commandId !== undefined && parsed.commandId !== expectedId) {
        setIsJsonValid(false);
      } else {
        setIsJsonValid(true);
      }
    } catch {
      setIsJsonValid(false);
    }
  };

  const toggleParam = (tmId: string, paramKey: string): void => {
    setParamStates((prev) => {
      const tmState = prev[tmId] ?? { checkedParams: [], validations: {} };
      const isChecked = tmState.checkedParams.includes(paramKey);
      if (isChecked) {
        // Keep validation config so unchecked dynamic array entries are still emitted with isValidate:false.
        return { ...prev, [tmId]: { ...tmState, checkedParams: tmState.checkedParams.filter((k) => k !== paramKey) } };
      }
      return {
        ...prev,
        [tmId]: {
          ...tmState,
          checkedParams: [...tmState.checkedParams, paramKey],
          validations: { ...tmState.validations, [paramKey]: tmState.validations[paramKey] ?? { type: "equal" as const, min: "0", max: "0", value: "0" } },
        },
      };
    });
  };

  // Registers or updates array group metadata and prunes validations/checkedParams for removed array indices
  const handleRegisterArrayGroup = (
    tmId: string,
    group: { basePath: string; itemType: "primitive" | "object"; objectFields: string[]; indices: number[] }
  ): void => {
    setParamStates((prev) => {
      const tmState = prev[tmId] ?? { checkedParams: [], validations: {} };
      const validIndexSet = new Set(group.indices);
      const arrayKeyRegex = new RegExp(`^${group.basePath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\[(\\d+)\\]`);

      // Prune checkedParams and validations for indices that were removed
      const nextChecked = tmState.checkedParams.filter((k) => {
        const m = k.match(arrayKeyRegex);
        return !m || validIndexSet.has(Number(m[1]));
      });

      const nextValidations: Record<string, ValidationRule> = {};
      Object.keys(tmState.validations).forEach((k) => {
        const m = k.match(arrayKeyRegex);
        if (!m || validIndexSet.has(Number(m[1]))) {
          nextValidations[k] = tmState.validations[k];
        }
      });

      const existingGroup = tmState.arrayGroups?.[group.basePath];
      const isIndicesSame =
        existingGroup &&
        existingGroup.indices.length === group.indices.length &&
        existingGroup.indices.every((val, idx) => val === group.indices[idx]);
      const isFieldsSame =
        existingGroup &&
        existingGroup.objectFields.length === group.objectFields.length &&
        existingGroup.objectFields.every((val, idx) => val === group.objectFields[idx]);
      const isCheckedSame = nextChecked.length === tmState.checkedParams.length;
      const isValidationsSame = Object.keys(nextValidations).length === Object.keys(tmState.validations).length;

      if (
        existingGroup &&
        isIndicesSame &&
        isFieldsSame &&
        isCheckedSame &&
        isValidationsSame &&
        existingGroup.itemType === group.itemType
      ) {
        return prev;
      }

      return {
        ...prev,
        [tmId]: {
          ...tmState,
          checkedParams: nextChecked,
          validations: nextValidations,
          arrayGroups: {
            ...(tmState.arrayGroups ?? {}),
            [group.basePath]: {
              basePath: group.basePath,
              itemType: group.itemType,
              objectFields: group.objectFields,
              indices: group.indices,
            },
          },
        },
      };
    });
  };

  const updateRule = (tmId: string, paramKey: string, updates: Partial<ValidationRule>): void => {
    setParamStates((prev) => ({
      ...prev,
      [tmId]: {
        ...prev[tmId],
        validations: { ...prev[tmId].validations, [paramKey]: { ...prev[tmId].validations[paramKey], ...updates } },
      },
    }));
  };

  const toggleAccordion = (tmId: string): void => {
    setExpandedTMs((prev) => (prev.includes(tmId) ? prev.filter((id) => id !== tmId) : [...prev, tmId]));
  };

  const handleSaveAndAddMore = (): void => {
    const delayMs = parseNonNegativeMs(delay);
    const timeoutMs = parseNonNegativeMs(timeout);
    if (delayMs > 0 && timeoutMs >= delayMs) {
      showToastMessage("error", "Timeout must be less than delay");
      return;
    }

    for (const tm of selectedTelemetries) {
      const tmState = paramStates[tm.label];
      if (!tmState) continue;
      for (const paramKey of tmState.checkedParams) {
        const rule = tmState.validations[paramKey];
        if (!rule) continue;
        const paramName = paramKey.split(".").pop() ?? paramKey;
        if (rule.type === "range") {
          if (rule.min === "" || rule.max === "") { showToastMessage("error", `Fill min and max for "${paramName}" in ${tm.label}`); return; }
        } else {
          if (rule.value === "") { showToastMessage("error", `Fill value for "${paramName}" in ${tm.label}`); return; }
        }
      }
    }

    const validationRule = selectedTelemetries.map((tm) => {
      const tmEntry = tm.value as TelemetryEntry;
      const p = flattenPayload((tm.value as { payload?: Record<string, unknown> })?.payload ?? {}, "", tmEntry?.payloadSchema);
      const enumMappings = flattenSchemaEnumMappings(tmEntry?.payloadSchema);
      const paramTypes = flattenSchemaTypes(tmEntry?.payloadSchema);
      return { label: tm.label, value: tmEntry, validation: buildNestedValidation(p, paramStates[tm.label], enumMappings, paramTypes) };
    });

    const newStep = { subsystem: component, teleData: selectedCommand, delay: normalizeMsInput(delay), timeout: normalizeMsInput(timeout), tcJson: tcJsonText, validationRule, isCritical };
    const editIdx = editingStepSnapshot?.idx ?? null;
    setTestCaseSteps((prev) => {
      if (editIdx !== null) {
        const next = [...prev];
        next.splice(editIdx, 0, newStep);
        return next;
      }
      return [...prev, newStep];
    });
    setEditingStepSnapshot(null);
    setComponent(busComponents[0] ?? null);
    setSelectedCommand(null);
    setDelay("6000");
    setTimeout("5000");
    setIsCritical(false);
  };

  const handleEditStep = (idx: number): void => {
    const step = testCaseSteps[idx];
    setEditingStepSnapshot({ step, idx });
    // Restore telemetries with schema re-attached from allTelemetries so all schema fields are visible
    const restoredValidationRules = step.validationRule.map((tm) => {
      const originalTm = allTelemetries.find((t) => getTelemetryIdentity(t) === getTelemetryIdentity(tm.value));
      return {
        label: tm.label,
        value: originalTm ? { ...tm.value, payloadSchema: originalTm.payloadSchema } : tm.value,
        validation: tm.validation,
      };
    });
    telemetriesToRestoreRef.current = restoredValidationRules.map((tm) => ({ label: tm.label, value: tm.value }));
    paramStatesToRestoreRef.current = reconstructParamStates(restoredValidationRules);
    tcJsonToRestoreRef.current = step.tcJson ?? null;
    setComponent(step.subsystem ?? null);
    setSelectedCommand(step.teleData ?? null);
    setDelay(step.delay ?? "");
    setTimeout(step.timeout ?? "");
    setIsCritical(step.isCritical ?? false);
    setTestCaseSteps((prev) => prev.filter((_, i) => i !== idx));
    setShowStepsDialog(false);
  };

  const handleAddTelecommand = (insertIdx?: number): void => {
    if (insertIdx !== undefined) {
      setEditingStepSnapshot({ step: null, idx: insertIdx });
    } else {
      setEditingStepSnapshot(null);
    }
    setComponent(busComponents[0] ?? null);
    setSelectedCommand(null);
    setDelay("6000");
    setTimeout("5000");
    setIsCritical(false);
    setSelectedTelemetries([]);
    setParamStates({});
    setShowStepsDialog(false);
  };

  const goBack = (): void => {
    navigateToWithState(`${mission_name}/${mission_id}/sat_details/${sat_name}/${sat_id}/Operations`, navigate, { tab: "Test Cases" });
  };

  const handleSaveConfirmed = async (): Promise<void> => {
    const allSteps = [...testCaseSteps];

    const payload = {
      testCaseName: editableTestCaseName || "Untitled Test Case",
      description: "Test case description",
      version: "1.0.0",
      testCaseDetails: allSteps.map((step) => {
        const teleData = step.teleData?.value as Record<string, unknown> | undefined;
        let parseSequenceData: Record<string, unknown> | null = null;
        try {
          parseSequenceData = step.tcJson ? JSON.parse(step.tcJson) : null;
        } catch {
          parseSequenceData = (teleData?.sequenceData as Record<string, unknown>) ?? null;
        }
        if (parseSequenceData && typeof parseSequenceData === "object" && "mode" in parseSequenceData) {
          if (parseSequenceData.mode === null || parseSequenceData.mode === undefined) {
            parseSequenceData = { ...parseSequenceData };
            delete parseSequenceData.mode;
          }
        }
        return {
          testCaseEntryType: "TC",
          isCritical: step.isCritical ?? false,
          testCaseEntryDetails: {
            commandName: (teleData?.name as string) ?? step.teleData?.label ?? "",
            sequenceData: parseSequenceData,
            delayAfterMS: parseNonNegativeMs(step.delay),
            timeoutMS: parseNonNegativeMs(step.timeout),
          },
          comment: "Default comment",
          subsystem: (() => {
            const resolved = findSubsystemByCommand(step.teleData as SelectOption | null);
            if (resolved?.label) return resolved.label;
            if (step.subsystem?.label && step.subsystem.label !== "All") return step.subsystem.label;
            return "";
          })(),
          validations: step.validationRule.map((rule) => {
            const ruleValueAny = rule.value as any;
            const { payloadSchema: _, ...rest } = ruleValueAny;
            const mode = ruleValueAny.mode ?? ruleValueAny.validationConfig?.mode;
            const item: Record<string, any> = {
              ...rest,
              payload: convertArraysToIndexedObjects(rule.validation),
            };
            if (mode !== undefined && mode !== null) {
              item.mode = mode;
            }
            return item;
          }),
        };
      }),
    };

    try {
      if (isEditMode && editTestCaseId) {
        await MissionService.putValidateTestCase(mission_id, editTestCaseId, sat_id, passedSatelliteId, payload);
      } else {
        await MissionService.postValidateTestCase(mission_id, sat_id, passedSatelliteId, payload);
      }
      showToastMessage("success", "Test case saved successfully");
      navigateToWithState(`${mission_name}/${mission_id}/sat_details/${sat_name}/${sat_id}/Operations`, navigate, { tab: "Test Cases", editedId: isEditMode ? editTestCaseId : null });
    } catch {
      showToastMessage("error", "Failed to save test case");
    }
  };

  const handleSave = (): void => {
    if (selectedCommand) {
      const delayMs = parseNonNegativeMs(delay);
      const timeoutMs = parseNonNegativeMs(timeout);
      if (delayMs > 0 && timeoutMs >= delayMs) {
        showToastMessage("error", "Timeout must be less than delay");
        return;
      }
      // Validate telemetries before saving the step
      for (const tm of selectedTelemetries) {
        const tmState = paramStates[tm.label];
        if (!tmState) continue;
        for (const paramKey of tmState.checkedParams) {
          const rule = tmState.validations[paramKey];
          if (!rule) continue;
          const paramName = paramKey.split(".").pop() ?? paramKey;
          if (rule.type === "range") {
            if (rule.min === "" || rule.max === "") { showToastMessage("error", `Fill min and max for "${paramName}" in ${tm.label}`); return; }
          } else {
            if (rule.value === "") { showToastMessage("error", `Fill value for "${paramName}" in ${tm.label}`); return; }
          }
        }
      }
      const validationRule = selectedTelemetries.map((tm) => {
        const tmEntry = tm.value as TelemetryEntry;
        const p = flattenPayload((tm.value as { payload?: Record<string, unknown> })?.payload ?? {}, "", tmEntry?.payloadSchema);
        const enumMappings = flattenSchemaEnumMappings(tmEntry?.payloadSchema);
        const paramTypes = flattenSchemaTypes(tmEntry?.payloadSchema);
        return { label: tm.label, value: tmEntry, validation: buildNestedValidation(p, paramStates[tm.label], enumMappings, paramTypes) };
      });
      let stepSubsystem = component;
      if (component?.label === "All") {
        const resolvedSubsystem = findSubsystemByCommand(selectedCommand);
        if (resolvedSubsystem) {
          stepSubsystem = resolvedSubsystem;
        }
      }
      const newStep = { subsystem: stepSubsystem, teleData: selectedCommand, delay: normalizeMsInput(delay), timeout: normalizeMsInput(timeout), tcJson: tcJsonText, validationRule, isCritical };
      const editIdx = editingStepSnapshot?.idx ?? null;
      setTestCaseSteps((prev) => {
        if (editIdx !== null) {
          const next = [...prev];
          next.splice(editIdx, 0, newStep);
          return next;
        }
        return [...prev, newStep];
      });
      setEditingStepSnapshot(null);
      setComponent(busComponents[0] ?? null);
      setSelectedCommand(null);
      setDelay("6000");
      setTimeout("5000");
      setIsCritical(false);
      // Blink the step row that was just saved (only when editing, not adding new)
      if (editIdx !== null) {
        if (blinkStepTimerRef.current) clearTimeout(blinkStepTimerRef.current);
        setBlinkStepIdx(editIdx);
        blinkStepTimerRef.current = window.setTimeout(() => setBlinkStepIdx(null), 2000);
      }
    }
    setShowStepsDialog(true);
  };

  // ── Clone / Move step handlers ────────────────────────────────
  const handleCloneStep = (idx: number): void => {
    setTestCaseSteps((prev) => {
      const stepToClone = prev[idx];
      const cloned: TestCaseStep = { ...stepToClone, validationRule: stepToClone.validationRule.map((r) => ({ ...r })) };
      const next = [...prev];
      next.splice(idx + 1, 0, cloned);
      return next;
    });
  };

  const handleReorderStep = (fromIdx: number, toIdx: number): void => {
    setTestCaseSteps((prev) => {
      if (fromIdx < 0 || toIdx < 0 || fromIdx >= prev.length || toIdx >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const handleDeleteStep = (idx: number): void => {
    setTestCaseSteps((prev) => prev.filter((_, i) => i !== idx));
  };

  const handlePasteSteps = (steps: TestCaseStep[]): void => {
    if (!steps || steps.length === 0) return;
    setTestCaseSteps((prev) => {
      const cloned = steps.map((s) => ({
        ...s,
        validationRule: Array.isArray(s.validationRule) ? s.validationRule.map((r) => ({ ...r })) : s.validationRule,
      }));
      const startIdx = prev.length;
      const blinkIdxs = cloned.map((_, i) => startIdx + i);
      if (blinkStepTimerRef.current) window.clearTimeout(blinkStepTimerRef.current);
      setBlinkStepIdx(blinkIdxs);
      blinkStepTimerRef.current = window.setTimeout(() => setBlinkStepIdx(null), 2000);
      return [...prev, ...cloned];
    });
  };

  // ── Derived flags (partial — isSave* defined after telemetryOptions below) ──
  const hasAnySelection = !!(selectedCommand || selectedTelemetries.length > 0);
  const hasSelectionChanges = !!(
    selectedCommand ||
    selectedTelemetries.length > 0 ||
    Object.keys(paramStates).length > 0 ||
    tcJsonText.trim().length > 0 ||
    delay !== "6000" ||
    timeout !== "5000"
  );
  const hasPreviewChanges = getStepsSignature(testCaseSteps) !== initialStepsSignatureRef.current;

  // ── Cancel / Discard button label & click ─────────────────────
  const cancelDiscardLabel = (showStepsDialog ? hasPreviewChanges : hasSelectionChanges) ? "Discard" : "Cancel";

  const handleCancelDiscardClick = (): void => {
    if (showStepsDialog) {
      if (hasPreviewChanges) {
        setShowDiscardConfirm(true);
      } else {
        goBack();
      }
      return;
    }
    if (hasSelectionChanges) {
      // Selection view with something selected → ask confirmation
      setShowDiscardConfirm(true);
      return;
    }
    // Nothing selected: Cancel → navigate directly without confirmation
    if (testCaseSteps.length > 0) {
      setShowStepsDialog(true);
    } else {
      goBack();
    }
  };

  const handleDiscardConfirmed = (): void => {
    setShowDiscardConfirm(false);
    if (showStepsDialog) {
      // Preview view discard
      goBack();
      return;
    }
    // Selection view discard
    const snapshot = editingStepSnapshot;
    setEditingStepSnapshot(null);
    setComponent(busComponents[0] ?? null);
    setSelectedCommand(null);
    setDelay("6000");
    setTimeout("5000");
    setIsCritical(false);
    setSelectedTelemetries([]);
    setParamStates({});
    if (snapshot) {
      if (snapshot.step) {
        // Restore the step that was being edited and go back to preview
        setTestCaseSteps((prev) => {
          const next = [...prev];
          next.splice(snapshot.idx, 0, snapshot.step!);
          return next;
        });
        // Blink the restored step row
        if (blinkStepTimerRef.current) clearTimeout(blinkStepTimerRef.current);
        setBlinkStepIdx(snapshot.idx);
        blinkStepTimerRef.current = window.setTimeout(() => setBlinkStepIdx(null), 2000);
      }
      setShowStepsDialog(true);
    } else if (testCaseSteps.length > 0) {
      setShowStepsDialog(true);
    } else {
      goBack();
    }
  };

  const handleParamStatesChange = (newStates: Record<string, TmState>): void => {
    setParamStates(newStates);
  };

  const handleExportValidations = (): void => {
    if (testCaseSteps.length === 0) {
      showToastMessage("error", "No test case steps to export");
      return;
    }
    const exported = exportTestCaseValidations(testCaseSteps, paramStates);
    const jsonStr = JSON.stringify(exported, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `testcase-validations-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToastMessage("success", "Validations exported successfully");
  };

  const handleImportValidations = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parseResult = parseExportedValidations(content);
        if (!parseResult.success) {
          showToastMessage("error", parseResult.error || "Failed to parse file");
          return;
        }

        if (!showStepsDialog) {
          showToastMessage("error", "Open test case steps to import validations");
          return;
        }

        const importResult = importValidationsFromFile(parseResult.data!, testCaseSteps.length, paramStates);
        if (importResult.success && importResult.states) {
          setParamStates(importResult.states);
          showToastMessage("success", "Validations imported successfully");
        } else {
          showToastMessage("error", importResult.error || "Failed to import validations");
        }
      } catch (err) {
        showToastMessage("error", "Error reading file");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const telemetryOptions: SelectOption[] = getTelemetryOptionsForCommand(selectedCommand, component);
  const telemetrySelectionKey = getTelemetryAutoSelectionKey(selectedCommand, telemetryOptions);
  const selectedTelemetrySeedKey = selectedTelemetries.map((tm) => `${tm.label}:${getTelemetryOptionIdentity(tm)}`).join("|");

  // Auto-select recommended TM after telemetry data arrives late.
  useEffect(() => {
    if (skipNextTelemetryAutoSelectRef.current) {
      skipNextTelemetryAutoSelectRef.current = false;
      return;
    }
    if (!selectedCommand || selectedTelemetries.length > 0 || telemetriesToRestoreRef.current.length > 0) return;
    const autoTelemetryOptions = getAutoTelemetryOptions(telemetryOptions);
    if (autoTelemetryOptions.length === 0 || autoTelemetrySelectionKeyRef.current === telemetrySelectionKey) return;
    autoTelemetrySelectionKeyRef.current = telemetrySelectionKey;
    setSelectedTelemetries(autoTelemetryOptions);
  }, [selectedCommand, selectedTelemetries.length, telemetrySelectionKey, telemetryOptions.length]);

  // Synchronizes param validation states: retains states for active TMs and seeds newly selected TMs
  useEffect(() => {
    if (selectedTelemetries.length === 0) {
      setParamStates((prev) => (Object.keys(prev).length > 0 ? {} : prev));
      return;
    }
    setParamStates((prev) => {
      let changed = false;
      const currentSelectedLabels = new Set(selectedTelemetries.map((t) => t.label));
      const next: Record<string, TmState> = {};

      // Only retain states for currently selected telemetries (prune unselected TMs)
      Object.keys(prev).forEach((key) => {
        if (currentSelectedLabels.has(key)) {
          next[key] = prev[key];
        } else {
          changed = true;
        }
      });

      selectedTelemetries.forEach((tm) => {
        if (next[tm.label]) return;
        const initialState = getInitialTmStateFromValidationConfig(tm.value);
        if (!initialState) {
          next[tm.label] = { checkedParams: [], validations: {} };
          changed = true;
          return;
        }
        next[tm.label] = initialState;
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [selectedTelemetrySeedKey, allTelemetries.length]);

  // Handles telemetry selection change and prunes unselected TM states so re-checking starts fresh
  const handleTelemetriesChange = (vals: MultiValue<SelectOption>): void => {
    const nextSelected = (vals ?? []) as SelectOption[];
    const nextSelectedLabels = new Set(nextSelected.map((tm) => tm.label));
    setSelectedTelemetries(nextSelected);
    setParamStates((prev) => {
      let changed = false;
      const next: Record<string, TmState> = {};
      Object.keys(prev).forEach((key) => {
        if (nextSelectedLabels.has(key)) {
          next[key] = prev[key];
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  // ── Save button flags (needs telemetryOptions) ─────────────────
  const currentStepComplete = !!selectedCommand;
  const isSaveDisabled = !currentStepComplete;
  const isSaveAndAddMoreDisabled = !currentStepComplete;

  return (
    <div className="tcd-container" data-testid="testcase-detail-container">
      <div className="tcd-main">
        <LeftPanel
          busComponents={busComponents}
          component={component}
          selectedCommand={selectedCommand}
          selectedTelemetries={selectedTelemetries}
          telemetryOptions={telemetryOptions}
          delay={delay}
          timeout={timeout}
          isCritical={isCritical}
          isComponentsLoading={loadingComponents}
          isTelemetriesLoading={!!selectedCommand && loadingTelemetries}
          forceCloseTelemetryMenu={showDiscardConfirm || showStepsDialog}
          onComponentChange={handleComponentChange}
          onTelecommandChange={handleTelecommandChange}
          onTelemetriesChange={handleTelemetriesChange}
          onDelayChange={setDelay}
          onTimeoutChange={setTimeout}
          onIsCriticalChange={setIsCritical}
        />

        <PreviewPanel
          testCaseSteps={testCaseSteps}
          selectedCommand={selectedCommand}
          busComponents={busComponents}
          tcJsonText={tcJsonText}
          isJsonValid={isJsonValid}
          selectedTelemetries={selectedTelemetries}
          paramStates={paramStates}
          expandedTMs={expandedTMs}
          activePreviewTab={activePreviewTab}
          activeTcPreviewTab={activeTcPreviewTab}
          testCaseName={editableTestCaseName}
          onTestCaseNameChange={setEditableTestCaseName}
          onJsonChange={handleJsonChange}
          onTcTabChange={setActiveTcPreviewTab}
          onToggleParam={toggleParam}
          onUpdateRule={updateRule}
          onRegisterArrayGroup={handleRegisterArrayGroup}
          onToggleAccordion={toggleAccordion}
          onTabChange={setActivePreviewTab}
          onShowSteps={() => setShowStepsDialog(true)}
          onClearCommand={handleCancelDiscardClick}
          onParamStatesChange={handleParamStatesChange}
        />
      </div>

      <div className={showStepsDialog ? undefined : 'tcd-steps-wrapper--hidden'}>
        <StepsDialog
          testCaseName={editableTestCaseName}
          testCaseSteps={testCaseSteps}
          selectedCommand={selectedCommand}
          component={component}
          delay={delay}
          timeout={timeout}
          isLoading={loadingEditSteps}
          busComponents={busComponents}
          onTestCaseNameChange={setEditableTestCaseName}
          onClose={handleCancelDiscardClick}
          onEditStep={handleEditStep}
          onAddTelecommand={handleAddTelecommand}
          onCloneStep={handleCloneStep}
          onReorderStep={handleReorderStep}
          onDeleteStep={handleDeleteStep}
          onPasteSteps={handlePasteSteps}
          blinkStepIdx={blinkStepIdx}
          showStepsDialog={showStepsDialog}
        />
      </div>

      <ConfirmationDialog
        type="discard"
        open={showDiscardConfirm}
        title="Discard Changes"
        description="Are you sure you want to discard? All unsaved changes will be lost."
        confirm_button_text="Discard"
        onConfirm={handleDiscardConfirmed}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={handleImportValidations}
      />

      <div className="tcd-footer">
        <div className="tcd-footer-left">
          <button
            className="tcd-footer-btn tcd-footer-btn--outline"
            type="button"
            onClick={handleCancelDiscardClick}
            data-testid="testcase-detail-cancel-btn"
          >
            {cancelDiscardLabel}
          </button>
        </div>
        <div className="tcd-footer-right">
          {showStepsDialog ? (
            <button className="tcd-footer-btn tcd-footer-btn--primary" type="button" onClick={handleSaveConfirmed} data-testid="testcase-detail-save-btn">Save Test Case</button>
          ) : (
            <>
              <button className="tcd-footer-btn tcd-footer-btn--outline" type="button" disabled={isSaveDisabled} onClick={handleSave} data-testid="testcase-detail-save-step-btn">Save</button>
              <button className="tcd-footer-btn tcd-footer-btn--primary" type="button" disabled={isSaveAndAddMoreDisabled} onClick={handleSaveAndAddMore} data-testid="testcase-detail-save-add-more-btn">Save &amp; Add More</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestCaseDetail;
